import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Modal } from './modal'
import { Spinner } from './spinner'
import { NumericInput } from './numeric-input'
import { Dropdown } from './dropdown'
import { Segmented } from './segmented'
import { FlowProgress } from './flow-progress'
import { BankMark, PlatformMark } from './platform-chip'
import { TokenIcon } from './holdings-table'
import { useToast } from './toast'
import { errorMessage, useTRPC } from '@/lib/trpc'
import { useRefreshActivity } from '@/lib/activity-feed'
import { krakenFeeItems } from '@/lib/activity'
import { formatUsd } from '@/lib/format'
import { USD_LOGO } from '@/lib/default-tokens'
import type { RouterOutputs } from '@/lib/api-types'

type BankOptions = RouterOutputs['kraken']['bankOptions']
type Method = BankOptions['methods'][number]
type Result = RouterOutputs['kraken']['withdrawUsd']

const STEPS = ['Requesting', 'On its way'] as const
const BANK_KEY = 'dispence.krakenBank'

/** the bank picked last time, local to this browser */
function rememberedBank(): string | null {
  try {
    return localStorage.getItem(BANK_KEY)
  } catch {
    return null
  }
}
function rememberBank(key: string) {
  try {
    localStorage.setItem(BANK_KEY, key)
  } catch {
    // storage blocked: the pick is not kept
  }
}

/** a dollar amount typed in; blanks, zeros and nonsense are nothing */
function parse(value: string): number | undefined {
  const n = Number(value)
  return value.trim() !== '' && Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : undefined
}

/** how a method reads to a person: its speed and what it costs (Kraken's names are "ACH (Plaid Transfer, via Plaid)", "RTP (...)", "... (Wire)") */
function speedOf(method: string): { label: string; eta: string } {
  if (/\bRTP\b|FedNow|instant/i.test(method)) return { label: 'Instant', eta: 'Arrives within minutes' }
  if (/wire/i.test(method)) return { label: 'Wire', eta: 'Arrives the same business day' }
  // Kraken's own site says 1 to 3 days for ACH (John, Nov 2 2026); its help article says within two business days
  if (/\bACH\b/i.test(method)) return { label: '1 to 3 business days', eta: 'Arrives in 1 to 3 business days' }
  return { label: method, eta: '' }
}
function feeOf(method: Method): string {
  if (method.feePct > 0) return `${method.feePct}%`
  if (method.feeFixed > 0) return formatUsd(method.feeFixed)
  return 'Free'
}

/**
 * Sends USD from Kraken to one of John's banks (Nov 2 2026): the banks
 * saved on Kraken, the ways there with their speed and fee, 25% / 50% /
 * Max of the USD held, and what arrives. ACH via Plaid is free and lands
 * in 1 to 3 business days; Kraken only offers the instant rail where a
 * bank is saved for it.
 */
export function KrakenWithdrawModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const trpc = useTRPC()
  const toast = useToast()
  const refresh = useRefreshActivity()
  const [amount, setAmount] = useState('')
  const [bank, setBank] = useState<string | null>(null)
  const [method, setMethod] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<Result | null>(null)
  useEffect(() => {
    if (open) {
      setAmount('')
      setError(null)
      setResult(null)
      setBank(rememberedBank())
      setMethod(null)
    }
  }, [open])
  const options = useQuery({ ...trpc.kraken.bankOptions.queryOptions(), enabled: open, staleTime: 60_000, refetchInterval: 60_000 })
  const o = options.data
  // only the ways there with a bank saved for them are on offer
  const offered = o ? o.methods.filter((m) => o.destinations.some((d) => d.method === m.method)) : []
  const destinations = o?.destinations ?? []
  const chosen = destinations.find((d) => d.key === bank) ?? null
  const currentMethod = method ?? chosen?.method ?? offered[0]?.method ?? null
  const banksFor = destinations.filter((d) => d.method === currentMethod)
  // the remembered bank, else the first one reachable the chosen way
  useEffect(() => {
    if (!o) return
    if (chosen && chosen.method === currentMethod) return
    const first = banksFor[0]
    if (first) setBank(first.key)
  }, [o, chosen, currentMethod, banksFor])
  const rule = offered.find((m) => m.method === currentMethod) ?? null
  const volume = parse(amount)
  const available = o?.usdAvailable ?? 0
  const quote = useQuery({
    ...trpc.kraken.withdrawQuote.queryOptions({ key: bank ?? '', amount: volume ?? 0 }),
    enabled: open && result === null && bank !== null && volume !== undefined,
    refetchInterval: 15_000,
    retry: false,
  })
  const q = volume !== undefined ? quote.data : undefined
  const tooMuch = volume !== undefined && volume > available + 1e-9
  const tooSmall = volume !== undefined && rule !== null && volume < rule.minimum
  const overLimit = q !== undefined && volume !== undefined && volume > q.limit + 1e-9
  const canSend = open && bank !== null && volume !== undefined && !tooMuch && !tooSmall && !overLimit && result === null
  const withdraw = useMutation(
    trpc.kraken.withdrawUsd.mutationOptions({
      onSuccess: (res) => {
        setResult(res)
        refresh(['kraken'])
        if (res.status === 'pending') {
          toast(
            'success',
            'Withdrawal on its way',
            <span className="inline-flex items-center gap-1.5">
              {formatUsd(res.netUsd)} to <BankMark name={res.destinationKey} size="size-4" /> {res.destinationKey}
            </span>,
          )
        }
      },
      onError: (err) => setError(errorMessage(err)),
    }),
  )
  function submit() {
    if (!bank) return setError('Pick a bank.')
    if (volume === undefined) return setError('Enter an amount.')
    setError(null)
    rememberBank(bank)
    withdraw.mutate({ key: bank, amount: volume })
  }
  const pct = (n: number) => setAmount((Math.floor(available * n) / 100).toFixed(2))
  const speed = currentMethod ? speedOf(currentMethod) : null
  const statusText = (res: Result) =>
    res.status === 'checked'
      ? 'Checked with Kraken. Nothing was sent on this run.'
      : res.status === 'failed'
        ? (res.error ?? 'Kraken could not send it.')
        : (
            <span className="inline-flex flex-wrap items-center justify-center gap-1.5">
              {formatUsd(res.netUsd)} is on its way to <BankMark name={res.destinationKey} size="size-4" /> {res.destinationKey}. {speedOf(res.method).eta}.
            </span>
          )

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          <PlatformMark id="kraken" size="size-5" />
          Withdraw USD
        </span>
      }
      footer={
        result ? (
          <button type="button" className="btn btn-primary w-full" onClick={onClose}>Done</button>
        ) : (
          <button type="button" className="btn btn-primary w-full" disabled={!canSend || withdraw.isPending} onClick={submit}>
            {withdraw.isPending ? 'Sending…' : volume !== undefined ? `Withdraw ${formatUsd(volume)}` : 'Withdraw'}
          </button>
        )
      }
    >
      <div className="grid gap-3">
        {withdraw.isPending || result ? (
          <FlowProgress
            labels={STEPS}
            step={result ? 1 : 0}
            done={result !== null && result.status !== 'failed'}
            failed={result?.status === 'failed'}
            status={result ? statusText(result) : `Sending ${formatUsd(volume ?? 0)} to ${bank ?? 'your bank'}…`}
            fees={result ? krakenFeeItems(result.feeUsd) : undefined}
          />
        ) : (
          <>
            <div className="card grid gap-3 p-4">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">Amount</span>
                <span className="flex gap-2 text-xs text-accent">
                  {[25, 50, 100].map((n) => (
                    <button key={n} type="button" onClick={() => pct(n)} disabled={!o}>
                      {n === 100 ? 'Max' : `${n}%`}
                    </button>
                  ))}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <NumericInput
                  className="w-0 min-w-0 flex-1 bg-transparent text-3xl font-semibold tabular-nums outline-none placeholder:text-muted"
                  placeholder="0.00"
                  value={amount}
                  onChange={setAmount}
                />
                <span className="pill flex items-center gap-2 bg-surface text-sm">
                  <TokenIcon symbol="USD" imageUrl={USD_LOGO} size="size-6" />
                  USD
                </span>
              </div>
              <div className="flex justify-end text-xs text-muted">
                {o ? <span>On Kraken {formatUsd(available)}</span> : <span className="skeleton inline-block h-3 w-24 align-middle" />}
              </div>
            </div>
            <div className="card grid gap-3 p-4">
              <span className="text-sm font-medium">To</span>
              {options.isPending ? (
                <span className="skeleton h-10 w-full" />
              ) : options.isError ? (
                <p className="text-sm text-negative">{errorMessage(options.error)}</p>
              ) : destinations.length === 0 ? (
                <p className="text-sm text-muted">No bank is saved on Kraken yet. Add one on Kraken's site first.</p>
              ) : (
                <>
                  {/* the speed only when there is a choice; Kraken offers the instant rail only where a bank is saved for it */}
                  {offered.length > 1 && currentMethod ? (
                    <Segmented
                      value={currentMethod}
                      onChange={(value) => {
                        setMethod(value)
                        setBank(destinations.find((d) => d.method === value)?.key ?? null)
                      }}
                      options={offered.map((m) => ({ value: m.method, label: `${speedOf(m.method).label} · ${feeOf(m)}` }))}
                      className="text-xs"
                    />
                  ) : null}
                  <Dropdown
                    value={bank ?? banksFor[0]?.key ?? ''}
                    options={banksFor.map((d) => ({
                      value: d.key,
                      label: (
                        <span className="flex items-center gap-2">
                          <BankMark name={d.key} size="size-5" />
                          {d.key}
                        </span>
                      ),
                    }))}
                    onChange={(key) => setBank(key)}
                    label="Bank"
                  />
                  {rule && speed ? (
                    <p className="text-xs text-muted">
                      {feeOf(rule)} · {speed.eta}
                    </p>
                  ) : null}
                </>
              )}
            </div>
            <div className="card grid gap-1 p-4 text-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted">Your bank receives</span>
                <span className="font-medium tabular-nums">
                  {volume !== undefined && quote.isFetching && !q ? <Spinner className="size-4" /> : q ? formatUsd(q.net) : volume !== undefined && rule ? formatUsd(Math.max(0, volume - rule.feeFixed - (volume * rule.feePct) / 100)) : '–'}
                </span>
              </div>
              {q && q.fee > 0 ? (
                <div className="flex items-center justify-between gap-3 text-xs text-muted">
                  <span>Fee</span>
                  <span className="tabular-nums">{formatUsd(q.fee)}</span>
                </div>
              ) : null}
            </div>
            {o?.dryRun ? <p className="text-xs text-muted">Test run: Kraken checks the request, nothing is sent.</p> : null}
            {tooMuch ? <p className="text-xs text-negative">More than the {formatUsd(available)} on Kraken.</p> : null}
            {tooSmall && rule ? <p className="text-xs text-negative">Kraken sends at least {formatUsd(rule.minimum)}.</p> : null}
            {overLimit && q ? <p className="text-xs text-negative">Kraken allows up to {formatUsd(q.limit)} right now.</p> : null}
            {quote.isError && volume !== undefined ? <p className="text-xs text-negative">{errorMessage(quote.error)}</p> : null}
            {error ? <p className="text-sm text-negative">{error}</p> : null}
          </>
        )}
      </div>
    </Modal>
  )
}
